import { useCallback, useEffect, useRef, useState } from "react";
import { type Location, useBlocker, useNavigate } from "react-router-dom";

type ValidateBeforeLeaveFn = (() => boolean | Promise<boolean>) | null;
type ResetFormFn = (() => void) | null;
type SubmitFormFn = (() => Promise<void>) | null;

const buildLocationUrl = (location: Location | null): string | null => {
  if (!location) {
    return null;
  }
  return location.pathname + location.search + (location.hash || "");
};

export const useUnsavedChangesGuardLogic = () => {
  const navigate = useNavigate();

  const [isDirty, setIsDirtyState] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const validateBeforeLeaveRef = useRef<ValidateBeforeLeaveFn>(null);
  const onResetRef = useRef<ResetFormFn>(null);
  const onSubmitRef = useRef<SubmitFormFn>(null);

  const isSavingRef = useRef(false);
  const isDirtyRef = useRef(false);
  const blockedLocationRef = useRef<Location | null>(null);

  const setIsDirty = useCallback((value: boolean) => {
    if (!isSavingRef.current) {
      isDirtyRef.current = value;
    }
    setIsDirtyState(value);
  }, []);

  const setValidateBeforeLeave = useCallback((fn: ValidateBeforeLeaveFn) => {
    validateBeforeLeaveRef.current = fn;
  }, []);

  const setOnReset = useCallback((fn: ResetFormFn) => {
    onResetRef.current = fn;
  }, []);

  const setOnSubmit = useCallback((fn: SubmitFormFn) => {
    onSubmitRef.current = fn;
  }, []);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const isNavigatingAway =
      currentLocation.pathname + currentLocation.search !== nextLocation.pathname + nextLocation.search;

    const shouldBlock = isDirtyRef.current && isNavigatingAway;

    if (shouldBlock) {
      blockedLocationRef.current = nextLocation;
    }

    return shouldBlock;
  });

  useEffect(() => {
    const handleBlockerStateChange = async () => {
      if (blocker.state === "blocked") {
        if (validateBeforeLeaveRef.current) {
          const isValid = await validateBeforeLeaveRef.current();
          if (!isValid) {
            blocker.reset?.();
            blockedLocationRef.current = null;
            return;
          }
        }
        setOpenConfirmationModal(true);
      } else if (!isSavingRef.current) {
        setOpenConfirmationModal(false);
        blockedLocationRef.current = null;
      }
    };

    handleBlockerStateChange();
  }, [blocker.state, blocker]);

  useEffect(() => {
    if (pendingNavigation && blocker.state !== "blocked") {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, blocker.state, navigate]);

  const executeNavigation = useCallback(
    (resetForm?: boolean) => {
      setOpenConfirmationModal(false);

      if (resetForm && onResetRef.current) {
        onResetRef.current();
      }

      isDirtyRef.current = false;
      setIsDirty(false);

      const targetUrl = buildLocationUrl(blockedLocationRef.current);
      blockedLocationRef.current = null;

      if (blocker.state === "blocked" && blocker.reset) {
        blocker.reset();
      }

      if (targetUrl) {
        setPendingNavigation(targetUrl);
      }
    },
    [blocker, setIsDirty]
  );

  const onConfirm = useCallback(() => executeNavigation(false), [executeNavigation]);
  const onCancel = useCallback(() => executeNavigation(true), [executeNavigation]);

  const onClose = useCallback(() => {
    setOpenConfirmationModal(false);
    blockedLocationRef.current = null;

    if (blocker.state === "blocked" && blocker.reset) {
      blocker.reset();
    }
  }, [blocker]);

  const handleSaveAndLeave = useCallback(async () => {
    if (!onSubmitRef.current) {
      return;
    }

    setSaveLoading(true);
    isSavingRef.current = true;

    try {
      await onSubmitRef.current();
      onConfirm();
    } catch (error) {
    } finally {
      setSaveLoading(false);
      isSavingRef.current = false;
    }
  }, [onConfirm]);

  return {
    openConfirmationModal,
    setOpenConfirmationModal,
    saveLoading,
    onConfirm,
    onCancel,
    onClose,
    handleSaveAndLeave,
    isDirty,
    setIsDirty,
    setValidateBeforeLeave,
    setOnReset,
    setOnSubmit,
    onSubmitRef,
  };
};
